// src/modules/gyms/contexts/gym.context.tsx

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { GymBranch, GymService, Amenity } from "../models/gym.model";
import { useGymData } from "../hooks/use-gym-data";
import { useBranchActions } from "../hooks/use-branch-actions";
import { useServiceActions } from "../hooks/use-service-actions";

interface GymPartnerContextValue {
  branches: GymBranch[];
  services: GymService[];
  amenities: Amenity[];
  loading: boolean;
  loaded: boolean;
  error: string | null;

  getBranchesByBrand: (brandId: string) => GymBranch[];
  getServicesByBrand: (brandId: string) => GymService[];
  loadServicesForBrand: (
    brandId: string,
    knownBranches?: Pick<GymBranch, "id" | "brandId">[],
  ) => Promise<void>;

  refresh: () => Promise<void>;
  branchActions: ReturnType<typeof useBranchActions>;
  serviceActions: ReturnType<typeof useServiceActions>;
}

const GymPartnerContext = createContext<GymPartnerContextValue | undefined>(
  undefined,
);

export function GymPartnerProvider({ children }: { children: ReactNode }) {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const authArgs = useMemo(
    () => ({ auth, setAuth, logout }),
    [auth, setAuth, logout],
  );

  const {
    branches,
    setBranches,
    services,
    setServices,
    amenities,
    loading,
    loaded,
    error,
    refresh,
    loadServicesForBrand,
  } = useGymData(authArgs);

  const branchActions = useBranchActions(setBranches, refresh, authArgs);
  const serviceActions = useServiceActions(setServices, refresh, authArgs);

  const getBranchesByBrand = useCallback(
    (brandId: string) => branches.filter((b) => b.brandId === brandId),
    [branches],
  );

  const getServicesByBrand = useCallback(
    (brandId: string) => services.filter((s) => s.brandId === brandId),
    [services],
  );

  const value: GymPartnerContextValue = {
    branches,
    services,
    amenities,
    loading,
    loaded,
    error,
    getBranchesByBrand,
    getServicesByBrand,
    loadServicesForBrand,
    refresh,
    branchActions,
    serviceActions,
  };

  return (
    <GymPartnerContext.Provider value={value}>
      {children}
    </GymPartnerContext.Provider>
  );
}

export function useGymPartner() {
  const context = useContext(GymPartnerContext);
  if (!context) {
    throw new Error("useGymPartner must be used within GymPartnerProvider");
  }
  return context;
}
